import { CompanyStampListRequestDto } from "@/dto/CompanyStampDto";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Router from "@/constants/router";

/**
 * 도장 관리 목록을 불러온다
 * @returns hooks
 */
export default function useList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyStampRequest, setCompanyStampRequest] =
    useState<CompanyStampListRequestDto>({
      page: parseInt(searchParams.get("page") || "1"),
      limit: parseInt(searchParams.get("limit") || "10"),
      comNm: searchParams.get("comNm") || undefined,
      admNm: searchParams.get("admNm") || undefined,
      productId: searchParams.get("productId") || undefined,
      templateId: searchParams.get("templateId") || undefined,
      subscriptionStat: searchParams.get("subscriptionStat") as
        | "PENDING"
        | "ACTIVE"
        | "CANCELED"
        | "EXPIRED"
        | undefined,
    });
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    parseInt(searchParams.get("limit") || "10")
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * 도장 상세 페이지 이동
   */
  const handleDetail = (comId: string) => {
    try {
      setIsLoading(true);
      navigate(Router.CompanyStampDetail.replace(":id", comId));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 페이지 변경
   */
  const handlePageChange = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    searchParams.set("page", (page + 1).toString());
    setSearchParams((prev) => {
      prev.set("page", (page + 1).toString());
      prev.set("limit", rowsPerPage.toString());
      return new URLSearchParams(prev);
    });
  };

  /**
   * 페이지 당 표시 항목 수 변경
   */
  const handleRowsPerPageChange = (
    d: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = d.target.value;

    setRowsPerPage(parseInt(newLimit));
    setSearchParams((prev) => {
      prev.set("limit", newLimit);
      prev.set("page", "1");
      return new URLSearchParams(prev);
    });
  };

  /**
   * 검색 조건 초기화
   */
  const handleReset = () => {
    setCompanyStampRequest({
      page: 1,
      limit: 10,
      comNm: undefined,
      admNm: undefined,
      productId: undefined,
      templateId: undefined,
      subscriptionStat: undefined,
    });
    setSearchParams((prev) => {
      prev.set("page", "1");
      prev.set("limit", "10");
      prev.delete("comNm");
      prev.delete("admNm");
      prev.delete("productId");
      prev.delete("templateId");
      prev.delete("subscriptionStat");
      return new URLSearchParams(prev);
    });
  };

  const handleSearch = (value: CompanyStampListRequestDto) => {
    setCompanyStampRequest(value);
    setSearchParams((prev) => {
      prev.set("page", "1");
      prev.set("limit", rowsPerPage.toString());

      if (value.comNm) {
        prev.set("comNm", value.comNm);
      } else {
        prev.delete("comNm");
      }

      if (value.admNm) {
        prev.set("admNm", value.admNm);
      } else {
        prev.delete("admNm");
      }

      if (value.productId) {
        prev.set("productId", value.productId);
      } else {
        prev.delete("productId");
      }

      if (value.templateId) {
        prev.set("templateId", value.templateId);
      } else {
        prev.delete("templateId");
      }

      if (value.subscriptionStat) {
        prev.set("subscriptionStat", value.subscriptionStat);
      } else {
        prev.delete("subscriptionStat");
      }

      return new URLSearchParams(prev);
    });
  };

  return {
    handleDetail,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleReset,
    rowsPerPage,
    companyStampRequest,
    isLoading,
  };
}
